import { call, takeEvery, put, delay } from 'redux-saga/effects';
import get from 'lodash/fp/get';
import FileSaver from 'file-saver';
import { createAction } from 'redux-actions';
import { http } from 'utils/http';
import { blobToDataURL, base64ToBlob } from 'utils/blob';
import urlJoin from 'url-join';
import { modalActions } from 'components/common/modal';
import { addLoader, removeLoader } from 'components/common/loaders/loading-actions';
import { Action, queryParametersType, constantQueryParamsType, headersType } from 'types/action';
import { API_REQUEST } from 'constants/action-types';
import { API_URL, API_GATEWAY_URL } from 'constants/urls';

const defaultNextType = {
  pending: 'REQUEST_PENDING',
  success: 'REQUEST_SUCCESS',
  error: 'REQUEST_ERROR'
};

type fetchResultType = { xhr?: any; body?: any; errors?: any; data?: any };

function* fetchData(action: Action) {
  const nextType = action.payload.nextActionType || defaultNextType;
  const loaderId = nextType.pending.replace(/([\w]+)_PENDING$/, '$1');

  try {
    const blobResponse = get('meta.blobResponse')(action);
    const arrayResponse = get('meta.arrayResponse')(action);
    const external = get('meta.external')(action);

    let constantQueryParams: constantQueryParamsType = {
      version: CONFIG.VERSION
    };

    let requestUrl = `${API_URL}${action.payload.url}`;
    if (external) {
      requestUrl = action.payload.url;
    }
    if (action.payload.getwayName) {
      requestUrl = `${API_GATEWAY_URL}${action.payload.getwayName}${
        action.payload.removeExtraApi ? '' : '/api'
      }${action.payload.url}`;
    }

    const queryParameters: queryParametersType = {};
    const headers: headersType = {};
    yield put(
      createAction(
        nextType.pending,
        () => action,
        () => action.meta
      )()
    );

    if (action.meta && action.meta.hasLoader) {
      yield put(addLoader(loaderId));
    }

    if (get('meta.pendingRedirect')(action)) {
      if (window.location.href === urlJoin(action.meta.pendingRedirect.url)) {
        window.location.reload();
      } else {
        window.location.href = urlJoin(action.meta.pendingRedirect.url);
      }
    }

    if (action.payload.contentType) {
      headers['Content-Type'] = action.payload.contentType;
    }

    let fetchResult: fetchResultType = yield call(
      http,
      action.payload.method,
      requestUrl,
      headers,
      action.payload.data,
      { ...action.payload.query, ...queryParameters, ...constantQueryParams },
      action.payload.files,
      blobResponse,
      arrayResponse
    );

    if (get('meta.downloadFromResponse')(action)) {
      const downloadUrl = fetchResult.xhr.response;
      if (downloadUrl) window.location.href = downloadUrl;
    }
    if (get('meta.downloadFileFromS3')(action)) {
      const fileUrl = JSON.parse(fetchResult.xhr.response).data;
      if (fileUrl) window.location.href = fileUrl;
    }
    if (get('meta.base64')(action)) {
      if (get('meta.downloadFile')(action)) {
        let blob = JSON.parse(fetchResult.xhr.response).data;
        if (!blob && JSON.parse(fetchResult.xhr.response)) {
          blob = base64ToBlob(JSON.parse(fetchResult.xhr.response));
        }
        yield FileSaver.saveAs(blob, action.meta.downloadFile.fileName);
      }
    }
    if (arrayResponse) {
      fetchResult = fetchResult.xhr.response;
    } else if (blobResponse) {
      if (get('meta.downloadFile')(action)) {
        yield FileSaver.saveAs(fetchResult.xhr.response, action.meta.downloadFile.fileName);
      }

      fetchResult = yield blobToDataURL(fetchResult.xhr.response);
    } else {
      fetchResult = fetchResult.body;
    }
    if (!fetchResult || !fetchResult.errors || fetchResult.errors.errorCode === 0) {
      yield put(
        createAction(
          nextType.success,
          () => fetchResult,
          () => action.meta
        )(fetchResult)
      );
      if (action.meta && action.meta.closeModalOnSuccess) {
        yield put(modalActions.closeModal());
      }
      if (action.meta && action.meta.hasLoader) {
        yield put(removeLoader(loaderId));
      }

      if (get('meta.redirect')(action)) {
        if (action.meta.redirect.afterToast) {
          yield delay(2000);
        }
        let addOns = [];
        if (action.meta.redirect.paramAddOns) {
          addOns = action.meta.redirect.paramAddOns.map(
            (item: string | number) => fetchResult.data[item]
          );
        }

        const redirectLink = action.meta.redirect.url
          ? urlJoin(action.meta.redirect.url, ...addOns)
          : urlJoin(...addOns);
        if (action.meta.redirect.blank) {
          window.open(redirectLink, '_blank');
        } else if (window.location.href === redirectLink) {
          window.location.reload();
        } else {
          window.location.href = redirectLink;
        }
      }
      if (action.payload.additionalActions) {
        if (action.meta && action.meta.delay) {
          yield delay(action.meta.delay);
        }
        for (let i = 0; i < action.payload.additionalActions.length; i += 1) {
          yield put(action.payload.additionalActions[i]);
        }
      }
    } else {
      throw fetchResult.errors.errorMessage;
    }
  } catch (e: any | Error) {
    yield put(
      createAction(
        nextType.error,
        () => action,
        () => action.meta
      )(e)
    );
    if (action.meta && action.meta.closeModalOnError) {
      yield put(modalActions.closeModal());
    }
    if (action.meta && action.meta.hasLoader) {
      yield put(removeLoader(loaderId));
    }
    if (action.payload.additionalActionsOnError) {
      for (let i = 0; i < action.payload.additionalActionsOnError.length; i += 1) {
        yield put(action.payload.additionalActionsOnError[i]);
      }
    }
    console.log(`error apiSaga ${e}`, action);
  }
}

export default function* apiSaga() {
  yield takeEvery(API_REQUEST, fetchData);
}
